import { Component } from '@theme/component';
import { isClickedOutside } from '@theme/utilities';

const DEFAULT_STORAGE_KEY = 'mr-barbershop-preferred-store';

/**
 * Header store selector — remembers the visitor's preferred location and opens Bokadirekt.
 *
 * @typedef {object} StoreSelectorRefs
 * @property {HTMLAnchorElement} [primaryLink]
 * @property {HTMLButtonElement} [toggleButton]
 * @property {HTMLSpanElement} [buttonLabel]
 * @property {HTMLElement} [panel]
 *
 * @extends {Component<StoreSelectorRefs>}
 */
class StoreSelectorComponent extends Component {
  /** @type {string} */
  #storageKey = DEFAULT_STORAGE_KEY;

  /** @type {string} */
  #buttonLabel = 'Boka tid';

  connectedCallback() {
    super.connectedCallback();
    this.#storageKey = this.dataset.storageKey || DEFAULT_STORAGE_KEY;
    this.#buttonLabel = this.dataset.buttonLabel || 'Boka tid';
    this.addEventListener('click', this.#handleStoreOptionClick);
    this.#applySavedStore();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#handleStoreOptionClick);
    this.#removeOutsideListener();
  }

  get isHidden() {
    return !this.refs.panel || this.refs.panel.hasAttribute('hidden');
  }

  /**
   * Primary button click — book at saved store or open the panel.
   *
   * @param {MouseEvent} event
   */
  handlePrimaryClick(event) {
    event.preventDefault();

    const savedStore = this.#getSavedStore();
    if (savedStore?.bookingUrl) {
      this.#openBooking(savedStore.bookingUrl);
      return;
    }

    this.showPanel();
  }

  /**
   * @param {MouseEvent} event
   */
  togglePanel(event) {
    event.preventDefault();
    event.stopPropagation();
    return this.isHidden ? this.showPanel() : this.hidePanel();
  }

  showPanel() {
    if (!this.refs.panel || !this.isHidden) return;

    this.#positionPanel();
    this.addEventListener('keyup', this.#handleKeyUp);
    window.addEventListener('resize', this.#positionPanel);
    window.addEventListener('scroll', this.#positionPanel, true);
    this.#addOutsideListener();

    this.classList.add('is-open');
    this.refs.panel.removeAttribute('hidden');
    this.refs.toggleButton?.setAttribute('aria-expanded', 'true');
  }

  hidePanel = () => {
    if (!this.refs.panel || this.isHidden) return;

    this.removeEventListener('keyup', this.#handleKeyUp);
    window.removeEventListener('resize', this.#positionPanel);
    window.removeEventListener('scroll', this.#positionPanel, true);
    this.#removeOutsideListener();

    this.classList.remove('is-open');
    this.refs.panel.setAttribute('hidden', '');
    this.refs.toggleButton?.setAttribute('aria-expanded', 'false');
    this.refs.panel.style.removeProperty('--header-store-panel-top');
    this.refs.panel.style.removeProperty('--header-store-panel-right');
    this.refs.panel.style.removeProperty('--header-store-panel-width');
  };

  /**
   * Delegated handler for store options rendered as links or buttons.
   *
   * @param {MouseEvent} event
   */
  #handleStoreOptionClick = (event) => {
    const option = event.target.closest('.header-store-selector__option[data-store-id]');
    if (!(option instanceof HTMLElement)) return;

    if (option.matches(':disabled') || option.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    const store = this.#readStoreFromElement(option);
    if (!store.id) return;

    this.#saveStore(store);
    this.#updateUi(store);

    if (this.refs.panel && !this.isHidden) {
      this.hidePanel();
    }

    if (option instanceof HTMLAnchorElement) {
      return;
    }

    event.preventDefault();

    if (store.bookingUrl) {
      this.#openBooking(store.bookingUrl);
    }
  };

  /**
   * @param {HTMLElement} element
   */
  #readStoreFromElement(element) {
    return {
      id: element.dataset.storeId || '',
      bookingUrl: element.dataset.bookingUrl || element.getAttribute('href') || '',
      shortName: element.dataset.shortName || '',
      name: element.dataset.name || '',
    };
  }

  #applySavedStore() {
    const savedStore = this.#getSavedStore();
    if (!savedStore) return;

    this.#updateUi(savedStore);
  }

  #getSavedStore() {
    try {
      const raw = localStorage.getItem(this.#storageKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  /**
   * @param {{ id: string, bookingUrl: string, shortName: string, name: string }} store
   */
  #saveStore(store) {
    localStorage.setItem(this.#storageKey, JSON.stringify(store));
  }

  /**
   * @param {{ id: string, bookingUrl: string, shortName: string, name: string }} store
   */
  #updateUi(store) {
    const label = store.shortName ? `${this.#buttonLabel} · ${store.shortName}` : this.#buttonLabel;

    if (this.refs.buttonLabel) {
      this.refs.buttonLabel.textContent = label;
    }

    if (this.refs.primaryLink && store.bookingUrl) {
      this.refs.primaryLink.href = store.bookingUrl;
    }

    this.querySelectorAll('.header-store-selector__option[data-store-id]').forEach((element) => {
      if (!(element instanceof HTMLElement)) return;
      const isSelected = element.dataset.storeId === store.id;
      element.setAttribute('aria-current', isSelected ? 'true' : 'false');
      element.classList.toggle('is-selected', isSelected);
    });
  }

  /**
   * @param {string} url
   */
  #openBooking(url) {
    const opened = window.open(url, '_blank', 'noopener,noreferrer');
    if (!opened) {
      window.location.assign(url);
    }
  }

  #positionPanel = () => {
    if (!this.refs.panel || !this.refs.toggleButton || this.isHidden) return;

    const anchor = this.refs.toggleButton.getBoundingClientRect();
    const panelWidth = Math.min(320, window.innerWidth - 16);

    this.refs.panel.style.setProperty('--header-store-panel-top', `${anchor.bottom + 8}px`);
    this.refs.panel.style.setProperty(
      '--header-store-panel-right',
      `${Math.max(window.innerWidth - anchor.right, 8)}px`
    );
    this.refs.panel.style.setProperty('--header-store-panel-width', `${panelWidth}px`);
  };

  #addOutsideListener() {
    setTimeout(() => {
      document.addEventListener('pointerdown', this.#handleClickOutside);
    }, 0);
  }

  #removeOutsideListener() {
    document.removeEventListener('pointerdown', this.#handleClickOutside);
  }

  /**
   * @param {KeyboardEvent} event
   */
  #handleKeyUp = (event) => {
    if (event.key === 'Escape') {
      this.hidePanel();
      event.stopPropagation();
      this.refs.toggleButton?.focus();
    }
  };

  /**
   * @param {PointerEvent} event
   */
  #handleClickOutside = (event) => {
    if (isClickedOutside(event, this)) {
      this.hidePanel();
    }
  };
}

if (!customElements.get('store-selector-component')) {
  customElements.define('store-selector-component', StoreSelectorComponent);
}
